import {
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  FacebookAuthProvider,
  getAdditionalUserInfo,
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithCredential,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut
} from "firebase/auth";
import {SIGNIN_TYPES} from "constants/AppConstants.js";
// @ts-ignore — web-app absolute import, resolved by the CRA bundler, not tsc.
import {auth} from "/src/FirebaseUtils.js";
import {Result} from "wrappers/Result.js";
import {FirebaseAuthentication} from "@capacitor-firebase/authentication";

export interface EmailPasswordCredential {
  email?: string;
  password?: string;
}

export default class AuthService {
  static async signInAsync(
    signInType: string,
    credential: EmailPasswordCredential = {},
    isNativePlatform: boolean = false
  ): Promise<Result<any>> {
    let result: any = null;
    try {
      if (SIGNIN_TYPES.emailAndPassword === signInType) {
        const {email, password} = credential;
        if (isNativePlatform) {
          result = await AuthService.signInWithEmailPasswordMobile(email, password);
        } else {
          result = await signInWithEmailAndPassword(auth, email, password);
        }
      }
      if (SIGNIN_TYPES.google === signInType) {
        if (isNativePlatform) {
          result = await AuthService.signInWithGoogleMobile();
        } else {
          const googleAuthProvider = new GoogleAuthProvider();
          result = await signInWithPopup(auth, googleAuthProvider);
        }
      }
      if (SIGNIN_TYPES.facebook === signInType) {
        if (isNativePlatform) {
          result = await AuthService.signInWithFacebookMobile();
        } else {
          const facebookAuthProvider = new FacebookAuthProvider();
          result = await signInWithPopup(auth, facebookAuthProvider);
        }
      }
      if (SIGNIN_TYPES.apple === signInType) {
        if (isNativePlatform) {
          result = await AuthService.signInWithAppleMobile();
        } else {
          const appleAuthProvider = new OAuthProvider("apple.com");
          result = await signInWithPopup(auth, appleAuthProvider);
        }
      }
      return Result.success(result);
    } catch (error) {
      return Result.failure(error);
    }
  }

  static async signOutAsync(isNativePlatform: boolean = false): Promise<boolean> {
    try {
      if (isNativePlatform) {
        // Sign out on the native layer
        await signOut(getAuth());
        await FirebaseAuthentication.signOut();
      }

      await auth.signOut();

      localStorage.setItem("WS_User", "");

      return true;
    } catch (error) {
      console.error("Error signing out", error);
      return false;
    }
  }

  static async sendPasswordResetLinkAsync(email: string, isNativePlatform: boolean = false): Promise<boolean> {
    try {
      if (isNativePlatform) {
        // Send password reset link on the native layer
        await FirebaseAuthentication.sendPasswordResetEmail({email});
      } else {
        await auth.sendPasswordResetEmail(email);
      }
      return true;
    } catch (error) {
      return false;
    }
  }

  static createCredentialFromAppleLoginAsync(result: any): {email: string; password: string} {
    const {sub} = result?.data?.additionalUserInfo?.profile ?? getAdditionalUserInfo(result.data).profile;
    const email = `${sub}@waterspeedapp.com`;
    const password = `${sub}Ws2020*`;
    return {email: email, password: password};
  }

  static async signWithRealAppleFlow(result: any) {
    const appleAuthProvider = new OAuthProvider("apple.com");
    const {oauthIdToken, oauthAccessToken, nonce} = result.data._tokenResponse;
    const credential = appleAuthProvider.credential({
      idToken: oauthIdToken,
      rawNonce: nonce,
      accessToken: oauthAccessToken
    });
    return await signInWithCredential(auth, credential);
  }

  static async signInWithCustomToken(token: string): Promise<Result<any>> {
    try {
      const result = await signInWithCustomToken(auth, token);
      return Result.success(result);
    } catch (error) {
      return Result.failure(error);
    }
  }

  static async signInWithEmailPasswordMobile(email: string, password: string) {
    // 1. Create credentials on the native layer
    const result = await FirebaseAuthentication.signInWithEmailAndPassword({
      email,
      password
    });
    // 2. Sign in on the web layer using the email and password
    const credential = EmailAuthProvider.credential(email, password);
    await signInWithCredential(getAuth(), credential);
    return result;
  }

  static async signInWithGoogleMobile() {
    // 1. Create credentials on the native layer
    const result = await FirebaseAuthentication.signInWithGoogle();
    // 2. Sign in on the web layer using the id token
    const credential = GoogleAuthProvider.credential(result.credential?.idToken);
    await signInWithCredential(getAuth(), credential);
    return result;
  }

  static async signInWithFacebookMobile() {
    // 1. Create credentials on the native layer
    const result = await FirebaseAuthentication.signInWithFacebook({scopes: ["email"]});
    // 2. Sign in on the web layer using the access token
    const credential = FacebookAuthProvider.credential(result.credential?.accessToken);
    await signInWithCredential(getAuth(), credential);
    return result;
  }

  static async signInWithAppleMobile() {
    // 1. Create credentials on the native layer
    const result = await FirebaseAuthentication.signInWithApple({
      skipNativeAuth: true
    });
    // 2. Sign in on the web layer using the id token and nonce
    const provider = new OAuthProvider("apple.com");
    const credential = provider.credential({
      idToken: result.credential?.idToken,
      rawNonce: result.credential?.nonce
    });
    await signInWithCredential(getAuth(), credential);
    return result;
  }

  static async createUserWithEmailAndPassword(
    email: string,
    password: string,
    isNativePlatform: boolean = false
  ): Promise<Result<any>> {
    try {
      if (isNativePlatform) {
        return FirebaseAuthentication.createUserWithEmailAndPassword({email, password}).then((nativeResult) => {
          // 2. Sign in on the web layer using the email and password
          const credential = EmailAuthProvider.credential(email, password);
          return signInWithCredential(getAuth(), credential).then(() => {
            return Result.success(nativeResult);
          });
        });
      } else {
        const result = await createUserWithEmailAndPassword(auth, email, password);
        return Result.success(result);
      }
    } catch (error) {
      return Result.failure(error);
    }
  }
}
